import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';

import { formatDateTime } from '@vlabs/shared/utils/date';
import { useTranslation } from 'react-i18next';

import type { EventId } from '@vlabs/api/luna-api/model';
import st from './SendAlarmButton.module.sass';

interface UseDelayParams {
  eventId: EventId;
}

interface SubmitTimeState {
  submitTime: number;
}

interface UseDelayResult {
  disabled: boolean;
  delayMessage: React.JSX.Element | null;
  setDelay: VoidFunction;
}

const DELAY_TIMEOUT = 3000;
const SUBMIT_TIME_STORAGE_KEY_PREFIX = 'alarm-submit-time';

function getSubmitTimeStorageKey(eventId: EventId): string {
  return `${SUBMIT_TIME_STORAGE_KEY_PREFIX}:${eventId}`;
}

export function getSubmitTime(eventId: EventId): SubmitTimeState | null {
  const value = sessionStorage.getItem(getSubmitTimeStorageKey(eventId));

  if (!value) return null;

  try {
    return JSON.parse(value) as SubmitTimeState;
  } catch {
    return null;
  }
}

export function saveSubmitTime(eventId: EventId, submitTimeState: SubmitTimeState): void {
  sessionStorage.setItem(
    getSubmitTimeStorageKey(eventId),
    JSON.stringify(submitTimeState),
  );
}

export function useDelay({ eventId }: UseDelayParams): UseDelayResult {
  const { t } = useTranslation();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [disabled, setDisabled] = useState(false);
  const [submitTimeState, setSubmitTimeState] = useState<SubmitTimeState | null>(() => (
    getSubmitTime(eventId)
  ));

  useEffect(() => {
    setSubmitTimeState(getSubmitTime(eventId));
    setDisabled(false);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, [eventId]);

  useEffect(() => () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  const setDelay = () => {
    const nextSubmitTimeState = {
      submitTime: Date.now(),
    };

    saveSubmitTime(eventId, nextSubmitTimeState);
    setSubmitTimeState(nextSubmitTimeState);
    setDisabled(true);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setDisabled(false);
    }, DELAY_TIMEOUT);
  };

  const formattedSubmitTime = useMemo(() => {
    if (!submitTimeState?.submitTime) return '';

    return formatDateTime(new Date(submitTimeState.submitTime));
  }, [submitTimeState?.submitTime]);

  const delayMessage = useMemo(() => {
    if (!formattedSubmitTime) return null;

    return (
      <div className={st.AlarmText}>
        <span>{t('features:alarm_button.timestamp_text')}</span>
        <span>{formattedSubmitTime}</span>
      </div>
    );
  }, [formattedSubmitTime, t]);

  return {
    disabled,
    delayMessage,
    setDelay,
  };
}
